/**
 * §5.9 level price: the SOL cost of each level-up, an S-curve from 0.001 SOL (level 1 → 2) to 0.02 SOL (99 → 100),
 * charged in ₽ at `RefundRouter.rcd_per_sol`. Mirrors `programs/redchain/src/level_price.rs`.
 */

/** `LEVEL_PRICE_LAMPORTS[n - 1]` is the price of growing from level `n` to `n + 1`. */
export const LEVEL_PRICE_LAMPORTS: readonly number[] = [
  1_000_000, 1_015_000, 1_031_000, 1_049_000, 1_069_000, 1_091_000, 1_116_000, 1_142_000, 1_172_000, 1_204_000, 1_240_000, 1_279_000,
  1_323_000, 1_370_000, 1_423_000, 1_480_000, 1_543_000, 1_613_000, 1_689_000, 1_772_000, 1_863_000, 1_963_000, 2_072_000, 2_191_000,
  2_320_000, 2_461_000, 2_615_000, 2_781_000, 2_961_000, 3_156_000, 3_367_000, 3_593_000, 3_836_000, 4_097_000, 4_376_000, 4_672_000,
  4_988_000, 5_321_000, 5_674_000, 6_044_000, 6_432_000, 6_836_000, 7_256_000, 7_691_000, 8_138_000, 8_597_000, 9_064_000, 9_539_000,
  10_018_000, 10_500_000, 10_982_000, 11_461_000, 11_936_000, 12_403_000, 12_862_000, 13_309_000, 13_744_000, 14_164_000, 14_568_000,
  14_956_000, 15_326_000, 15_679_000, 16_012_000, 16_328_000, 16_624_000, 16_903_000, 17_164_000, 17_407_000, 17_633_000, 17_844_000,
  18_039_000, 18_219_000, 18_385_000, 18_539_000, 18_680_000, 18_809_000, 18_928_000, 19_037_000, 19_137_000, 19_228_000, 19_311_000,
  19_387_000, 19_457_000, 19_520_000, 19_577_000, 19_630_000, 19_677_000, 19_721_000, 19_760_000, 19_796_000, 19_828_000, 19_858_000,
  19_884_000, 19_909_000, 19_931_000, 19_951_000, 19_969_000, 19_985_000, 20_000_000,
];

/** Lamports to grow from level `from` to level `to` (`1 ≤ from < to ≤ 100`). */
export function levelsPriceLamports(from: number, to: number): bigint {
  if (!(Number.isInteger(from) && Number.isInteger(to) && from >= 1 && from < to && to <= 100)) throw new RangeError(`invalid level range ${from} → ${to}`);
  let sum = 0n;
  for (let n = from; n < to; n++) sum += BigInt(LEVEL_PRICE_LAMPORTS[n - 1]!);
  return sum;
}

/** ₽ the program charges for `lamports` at `rcdPerSol`, rounded up (`RefundRouter::charge_quote`). */
export function chargeQuoteRcd(lamports: bigint, rcdPerSol: bigint): bigint {
  return (lamports * rcdPerSol + 999_999_999n) / 1_000_000_000n;
}

export function levelsPriceRcd(from: number, to: number, rcdPerSol: bigint): bigint {
  return chargeQuoteRcd(levelsPriceLamports(from, to), rcdPerSol);
}

/** ₽ per SOL the ROM patch prices with when the refund router has no rate (and for offline play). */
export const FALLBACK_RCD_PER_SOL = 10_000_000n;

/**
 * The patched Day Care's per-level ₽ table (§3.5): entry n − 1 is the price of level n → n + 1, capped at the
 * in-game maximum of ₽999,999. The game sums the entries for the levels grown.
 */
export function dayCarePriceTable(rcdPerSol: bigint): number[] {
  return LEVEL_PRICE_LAMPORTS.map((l) => {
    const p = chargeQuoteRcd(BigInt(l), rcdPerSol);
    return Number(p > 999_999n ? 999_999n : p);
  });
}
